import React, { useState } from 'react';
import CabinCrewLayout from '../layouts/CabinCrewLayout';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  Scissors, Sparkles, Eye, Hand, Shirt, CheckCircle, XCircle,
  Info, AlertTriangle
} from 'lucide-react';

export default function CabinCrewCareerGrooming() {
  const [selectedAirline, setSelectedAirline] = useState('THY');
  const [selectedGender, setSelectedGender] = useState('female');

  const airlines = {
    THY: { name: 'Türk Hava Yolları', color: 'bg-red-500', logo: '🇹🇷' },
    Emirates: { name: 'Emirates', color: 'bg-red-600', logo: '🇦🇪' },
    Qatar: { name: 'Qatar Airways', color: 'bg-purple-700', logo: '🇶🇦' }
  };

  // Grooming standartları - havayollarına göre
  const groomingStandards = {
    THY: {
      female: {
        hair: {
          title: 'Saç Standartları',
          rules: [
            { text: 'Omuz üstü saçlar serbest bırakılabilir', allowed: true },
            { text: 'Omuz altı saçlar topuz veya at kuyruğu yapılmalı', allowed: true },
            { text: 'Doğal renkler tercih edilmeli (siyah, kahve, sarı tonları)', allowed: true },
            { text: 'Uç boyama (ombre, balayage) ölçülü olabilir', allowed: true },
            { text: 'Pembe, mavi, yeşil gibi doğal olmayan renkler yasak', allowed: false },
            { text: 'Saç aksesuarları THY standartlarına uygun olmalı', allowed: true }
          ],
          tips: 'THY klasik topuz veya Fransız topuzunu tercih eder. Saç tokaları koyu renk olmalı.'
        },
        makeup: {
          title: 'Makyaj Standartları',
          rules: [
            { text: 'Doğal ve profesyonel makyaj zorunlu', allowed: true },
            { text: 'Fondöten cilt tonuna uygun olmalı', allowed: true },
            { text: 'Kırmızı veya nude ruj kullanılabilir', allowed: true },
            { text: 'Smoky eye hafif uygulanabilir', allowed: true },
            { text: 'Aşırı parlak veya glitterli ürünler yasak', allowed: false },
            { text: 'Takma kirpik doğal görünümlü olmalı', allowed: true }
          ],
          tips: 'THY kırmızı ruju teşvik eder. Mat veya saten finish tercih edilir.'
        },
        nails: {
          title: 'Tırnak Standartları',
          rules: [
            { text: 'Kısa veya orta uzunlukta tırnaklar', allowed: true },
            { text: 'Şeffaf, nude veya kırmızı oje', allowed: true },
            { text: 'French manikür kabul edilir', allowed: true },
            { text: 'Neon renkler ve nail art yasak', allowed: false },
            { text: 'Tırnaklar temiz ve bakımlı olmalı', allowed: true }
          ],
          tips: 'THY standartlarında kırmızı oje klasik tercihtir.'
        },
        accessories: {
          title: 'Aksesuar & Kıyafet',
          rules: [
            { text: 'Küçük ve zarif küpeler (tek çift)', allowed: true },
            { text: 'İnce zincir kolye kabul edilir', allowed: true },
            { text: 'Nişan/evlilik yüzüğü takılabilir', allowed: true },
            { text: 'Büyük, sallanan küpeler yasak', allowed: false },
            { text: 'Siyah veya lacivert çorap zorunlu', allowed: true },
            { text: 'Topuklu ayakkabı (3-5 cm) zorunlu', allowed: true }
          ],
          tips: 'Üniforma ile uyumlu, minimal aksesuarlar tercih edilir.'
        }
      },
      male: {
        hair: {
          title: 'Saç Standartları',
          rules: [
            { text: 'Kısa ve düzgün kesim zorunlu', allowed: true },
            { text: 'Kulakları ve yakayı kapatmamalı', allowed: true },
            { text: 'Doğal saç rengi tercih edilir', allowed: true },
            { text: 'Jöle veya wax ile şekillendirilebilir', allowed: true },
            { text: 'Uzun saç, at kuyruğu veya topuz yasak', allowed: false },
            { text: 'Sakal yasak, bıyık ölçülü olabilir', allowed: false }
          ],
          tips: 'Klasik kısa kesim, yanlarda kısa üstte biraz uzun tercih edilir.'
        },
        grooming: {
          title: 'Bakım Standartları',
          rules: [
            { text: 'Her gün tıraş olunmalı (temiz yüz)', allowed: true },
            { text: 'Bıyık dudak üst çizgisini geçmemeli', allowed: true },
            { text: 'Favori uzunluğu kulak ortasını geçmemeli', allowed: true },
            { text: 'Sakal kesinlikle yasak', allowed: false },
            { text: 'Kirli sakal görünümü yasak', allowed: false }
          ],
          tips: 'THY erkek kabin ekibinde temiz tıraşlı yüz beklenir.'
        },
        accessories: {
          title: 'Aksesuar & Kıyafet',
          rules: [
            { text: 'Düz siyah veya lacivert kravat', allowed: true },
            { text: 'Klasik kol saati takılabilir', allowed: true },
            { text: 'Nişan/evlilik yüzüğü takılabilir', allowed: true },
            { text: 'Küpe, piercing kesinlikle yasak', allowed: false },
            { text: 'Siyah deri ayakkabı zorunlu', allowed: true },
            { text: 'Lacivert veya siyah çorap', allowed: true }
          ],
          tips: 'Minimal ve profesyonel görünüm esastır.'
        }
      }
    },
    Emirates: {
      female: {
        hair: {
          title: 'Saç Standartları',
          rules: [
            { text: 'Saçlar kesinlikle toplanmalı - topuz zorunlu', allowed: true },
            { text: 'Emirates standart topuz şekli uygulanmalı', allowed: true },
            { text: 'Saç rengi doğal tonlarda olmalı', allowed: true },
            { text: 'Saç ağı (hairnet) kullanımı zorunlu', allowed: true },
            { text: 'Serbest saç kesinlikle yasak', allowed: false },
            { text: 'Kırmızı şapka (pillbox hat) ile uyumlu olmalı', allowed: true }
          ],
          tips: 'Emirates\'in ikonik kırmızı şapkası için saç mutlaka topuz yapılmalı.'
        },
        makeup: {
          title: 'Makyaj Standartları',
          rules: [
            { text: 'Emirates kırmızısı ruj zorunlu', allowed: true },
            { text: 'Belirgin ama profesyonel göz makyajı', allowed: true },
            { text: 'Fondöten ve pudra kusursuz uygulanmalı', allowed: true },
            { text: 'Allık ölçülü kullanılmalı', allowed: true },
            { text: 'Doğal görünüm veya makyajsız yasak', allowed: false }
          ],
          tips: 'Emirates makyajda kırmızı rujan vurgusunu çok önemser.'
        },
        nails: {
          title: 'Tırnak Standartları',
          rules: [
            { text: 'Kırmızı oje zorunlu (Emirates kırmızısı)', allowed: true },
            { text: 'Tırnaklar orta uzunlukta ve bakımlı', allowed: true },
            { text: 'French manikür kabul edilmez', allowed: false },
            { text: 'Diğer renkler kabul edilmez', allowed: false }
          ],
          tips: 'Emirates için sadece kırmızı oje kullanılır.'
        },
        accessories: {
          title: 'Aksesuar & Kıyafet',
          rules: [
            { text: 'Altın veya gümüş ince küpe', allowed: true },
            { text: 'İnce zincir kolye', allowed: true },
            { text: 'Bej rengi çorap zorunlu', allowed: true },
            { text: 'Siyah topuklu ayakkabı (5-7 cm)', allowed: true },
            { text: 'Kırmızı pillbox şapka ile üniforma', allowed: true }
          ],
          tips: 'Emirates üniforması dünyada en tanınır. Şık ve profesyonel görünüm esastır.'
        }
      },
      male: {
        hair: {
          title: 'Saç Standartları',
          rules: [
            { text: 'Kısa ve düzenli kesim', allowed: true },
            { text: 'Yanlarda çok kısa, üstte düzgün', allowed: true },
            { text: 'Saç jölesi veya wax kullanılabilir', allowed: true },
            { text: 'Uzun saç yasak', allowed: false }
          ],
          tips: 'Profesyonel ve temiz görünüm beklenir.'
        },
        grooming: {
          title: 'Bakım Standartları',
          rules: [
            { text: 'Temiz tıraşlı yüz zorunlu', allowed: true },
            { text: 'Sakal kesinlikle yasak', allowed: false },
            { text: 'Bıyık yasak', allowed: false },
            { text: 'Favori kısa tutulmalı', allowed: true }
          ],
          tips: 'Emirates erkek ekibinde kesinlikle temiz yüz beklenir.'
        },
        accessories: {
          title: 'Aksesuar & Kıyafet',
          rules: [
            { text: 'Emirates kravat ve rozet', allowed: true },
            { text: 'Klasik kol saati', allowed: true },
            { text: 'Siyah deri ayakkabı', allowed: true },
            { text: 'Piercing, küpe yasak', allowed: false }
          ],
          tips: 'Emirates erkek üniforması oldukça şık ve profesyoneldir.'
        }
      }
    },
    Qatar: {
      female: {
        hair: {
          title: 'Saç Standartları',
          rules: [
            { text: 'Saçlar toplanmalı veya kısa olmalı', allowed: true },
            { text: 'Qatar pilotka şapkası ile uyumlu', allowed: true },
            { text: 'Doğal saç renkleri', allowed: true },
            { text: 'Saç ağı tavsiye edilir', allowed: true },
            { text: 'Aşırı kabarık veya uzun serbest saç yasak', allowed: false }
          ],
          tips: 'Qatar Airways şık topuz ve pilotka kombinasyonunu tercih eder.'
        },
        makeup: {
          title: 'Makyaj Standartları',
          rules: [
            { text: 'Bordo/koyu kırmızı ruj tercih edilir', allowed: true },
            { text: 'Profesyonel ve şık makyaj', allowed: true },
            { text: 'Göz makyajı vurgulu olabilir', allowed: true },
            { text: 'Çok açık veya neon tonlar yasak', allowed: false }
          ],
          tips: 'Qatar\'ın bordo üniformasıyla uyumlu makyaj beklenir.'
        },
        nails: {
          title: 'Tırnak Standartları',
          rules: [
            { text: 'Bordo veya nude oje', allowed: true },
            { text: 'Bakımlı ve orta uzunlukta', allowed: true },
            { text: 'Parlak veya neon renkler yasak', allowed: false }
          ],
          tips: 'Üniforma rengiyle uyumlu tonlar tercih edilir.'
        },
        accessories: {
          title: 'Aksesuar & Kıyafet',
          rules: [
            { text: 'Zarif küpe ve kolye', allowed: true },
            { text: 'Bej çorap', allowed: true },
            { text: 'Bordo pilotka şapka', allowed: true },
            { text: 'Siyah topuklu ayakkabı', allowed: true }
          ],
          tips: 'Qatar üniforması çok şık olup, aksesuarlar minimal tutulmalı.'
        }
      },
      male: {
        hair: {
          title: 'Saç Standartları',
          rules: [
            { text: 'Kısa ve düzgün kesim', allowed: true },
            { text: 'Profesyonel görünüm', allowed: true },
            { text: 'Uzun saç yasak', allowed: false }
          ],
          tips: 'Klasik profesyonel görünüm beklenir.'
        },
        grooming: {
          title: 'Bakım Standartları',
          rules: [
            { text: 'Temiz tıraşlı yüz', allowed: true },
            { text: 'Düzenli bıyık kabul edilebilir', allowed: true },
            { text: 'Sakal yasak', allowed: false }
          ],
          tips: 'Qatar erkek ekibinde düzenli bıyık kabul edilir.'
        },
        accessories: {
          title: 'Aksesuar & Kıyafet',
          rules: [
            { text: 'Qatar kravat ve rozet', allowed: true },
            { text: 'Klasik saat', allowed: true },
            { text: 'Piercing yasak', allowed: false }
          ],
          tips: 'Profesyonel ve şık görünüm esastır.'
        }
      }
    }
  };

  const currentStandards = groomingStandards[selectedAirline][selectedGender];

  return (
    <CabinCrewLayout>
      <div className="min-h-screen bg-gradient-to-br from-purple-50 via-pink-50 to-rose-50 p-4">
        <div className="container mx-auto max-w-6xl">
          {/* Header */}
          <div className="mb-8">
            <h1 className="text-3xl font-black text-gray-800 flex items-center gap-3">
              <Sparkles className="w-8 h-8 text-purple-500" />
              İmaj & Grooming Standartları
            </h1>
            <p className="text-gray-600">Havayolu şirketlerinin görünüm standartlarını öğrenin</p>
          </div>

          {/* Filters */}
          <div className="flex flex-wrap gap-4 mb-6">
            <div className="flex gap-2">
              {Object.entries(airlines).map(([key, airline]) => (
                <Button
                  key={key}
                  variant={selectedAirline === key ? 'default' : 'outline'}
                  onClick={() => setSelectedAirline(key)}
                  className={selectedAirline === key ? airline.color : ''}
                >
                  <span className="mr-2">{airline.logo}</span>
                  {airline.name}
                </Button>
              ))}
            </div>
            <div className="flex gap-2 ml-auto">
              <Button
                variant={selectedGender === 'female' ? 'default' : 'outline'}
                onClick={() => setSelectedGender('female')}
                className={selectedGender === 'female' ? 'bg-pink-500' : ''}
              >
                👩 Kadın
              </Button>
              <Button
                variant={selectedGender === 'male' ? 'default' : 'outline'}
                onClick={() => setSelectedGender('male')}
                className={selectedGender === 'male' ? 'bg-blue-500' : ''}
              >
                👨 Erkek
              </Button>
            </div>
          </div>

          {/* Standards Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {Object.entries(currentStandards).map(([key, section]) => (
              <Card key={key} className="border-2">
                <CardHeader className="pb-2">
                  <CardTitle className="flex items-center gap-2 text-lg">
                    {key === 'hair' && <Scissors className="w-5 h-5 text-purple-500" />}
                    {key === 'makeup' && <Sparkles className="w-5 h-5 text-pink-500" />}
                    {key === 'grooming' && <Sparkles className="w-5 h-5 text-blue-500" />}
                    {key === 'nails' && <Hand className="w-5 h-5 text-rose-500" />}
                    {key === 'accessories' && <Shirt className="w-5 h-5 text-indigo-500" />}
                    {section.title}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2 mb-4">
                    {section.rules.map((rule, index) => (
                      <div key={index} className="flex items-start gap-2">
                        {rule.allowed ? (
                          <CheckCircle className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                        ) : (
                          <XCircle className="w-5 h-5 text-red-500 mt-0.5 flex-shrink-0" />
                        )}
                        <span className={rule.allowed ? 'text-gray-700' : 'text-gray-500 line-through'}>
                          {rule.text}
                        </span>
                      </div>
                    ))}
                  </div>
                  <div className="bg-amber-50 rounded-lg p-3">
                    <p className="text-sm text-amber-800">
                      <Info className="w-4 h-4 inline mr-1" />
                      {section.tips}
                    </p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Comparison Table */}
          <Card className="mt-8">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Eye className="w-5 h-5 text-blue-500" />
                Havayolu Karşılaştırması - {selectedGender === 'female' ? 'Kadın' : 'Erkek'}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left p-3">Kriter</th>
                      {Object.entries(airlines).map(([key, airline]) => (
                        <th key={key} className="text-center p-3">
                          <span className="mr-1">{airline.logo}</span>
                          {airline.name}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="border-b">
                      <td className="p-3 font-medium">Ruj Rengi</td>
                      <td className="text-center p-3">Kırmızı/Nude</td>
                      <td className="text-center p-3">Kırmızı (Zorunlu)</td>
                      <td className="text-center p-3">Bordo</td>
                    </tr>
                    <tr className="border-b">
                      <td className="p-3 font-medium">Saç Stili</td>
                      <td className="text-center p-3">Topuz veya Serbest (kısa)</td>
                      <td className="text-center p-3">Topuz (Zorunlu)</td>
                      <td className="text-center p-3">Topuz</td>
                    </tr>
                    <tr className="border-b">
                      <td className="p-3 font-medium">Oje Rengi</td>
                      <td className="text-center p-3">Kırmızı/Nude</td>
                      <td className="text-center p-3">Kırmızı (Zorunlu)</td>
                      <td className="text-center p-3">Bordo/Nude</td>
                    </tr>
                    <tr className="border-b">
                      <td className="p-3 font-medium">Şapka</td>
                      <td className="text-center p-3">-</td>
                      <td className="text-center p-3">Kırmızı Pillbox</td>
                      <td className="text-center p-3">Bordo Pilotka</td>
                    </tr>
                    <tr className="border-b">
                      <td className="p-3 font-medium">Çorap Rengi</td>
                      <td className="text-center p-3">Siyah/Lacivert</td>
                      <td className="text-center p-3">Bej</td>
                      <td className="text-center p-3">Bej</td>
                    </tr>
                    {selectedGender === 'male' && (
                      <>
                        <tr className="border-b">
                          <td className="p-3 font-medium">Sakal</td>
                          <td className="text-center p-3">❌ Yasak</td>
                          <td className="text-center p-3">❌ Yasak</td>
                          <td className="text-center p-3">❌ Yasak</td>
                        </tr>
                        <tr className="border-b">
                          <td className="p-3 font-medium">Bıyık</td>
                          <td className="text-center p-3">✅ İzinli (Ölçülü)</td>
                          <td className="text-center p-3">❌ Yasak</td>
                          <td className="text-center p-3">✅ İzinli</td>
                        </tr>
                      </>
                    )}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>

          {/* Warning */}
          <Card className="mt-6 bg-amber-50 border-amber-200">
            <CardContent className="p-4">
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-amber-500 mt-0.5" />
                <div>
                  <p className="font-medium text-amber-800">Önemli Uyarı</p>
                  <p className="text-sm text-amber-700">
                    Bu bilgiler genel rehber niteliğindedir. Her havayolu şirketinin standartları 
                    zaman zaman güncellenebilir. Başvuru öncesi şirketin resmi kariyer sayfasından 
                    güncel grooming standartlarını kontrol etmenizi öneririz.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </CabinCrewLayout>
  );
}
